import { onMounted, Ref } from 'vue'
import { Direction, oppositeDirection } from '../server-src/model'

const MIN_DISTANCE_TOUCHMOVE = 10
const MAX_TIME_TOUCHMOVE = 500
const MIN_RATIO_TOUCHMOVE = 1.5

// These apply when you're only doing a single swipe, then lifting your finger
const MIN_DISTANCE_SINGLETOUCH = 2
const MAX_TIME_SINGLETOUCH = 1000
const MIN_RATIO_SINGLETOUCH = 0.3

export function useSwipes(
  div: Ref<null | HTMLDivElement>,
  started: Ref<boolean>,
  currentDirection: Ref<Direction | undefined>,
  turn: (direction: Direction) => void
) {
  onMounted(() => {
    const gestureZone = div.value
    if (gestureZone != null) {
      let touchstartX = 0
      let touchstartY = 0
      let touchendX = 0
      let touchendY = 0

      let touchstarttime = 0
      let prevtouchendtime = 0

      gestureZone.addEventListener(
        'touchmove',
        function (event) {
          if (started.value) {
            touchendX = event.changedTouches[0].screenX
            touchendY = event.changedTouches[0].screenY
            if (
              handleGesture() ||
              performance.now() - touchstarttime > MAX_TIME_TOUCHMOVE
            ) {
              touchstartX = touchendX
              touchstartY = touchendY
              touchstarttime = performance.now()
            }
            prevtouchendtime = touchstarttime
          }
        },
        false
      )

      gestureZone.addEventListener(
        'touchstart',
        function (event) {
          touchstartX = event.changedTouches[0].screenX
          touchstartY = event.changedTouches[0].screenY
          touchstarttime = performance.now()
          touchendX = touchstartX
          touchendY = touchstartY
          prevtouchendtime = touchstarttime
        },
        false
      )

      gestureZone.addEventListener(
        'touchend',
        function (event) {
          touchendX = event.changedTouches[0].screenX
          touchendY = event.changedTouches[0].screenY
          if (started.value) {
            event.preventDefault()
            handleGesture(turnsInCurrentSwipe.length === 0)
          }
          touchstartX = 0
          touchendX = 0
          touchstartY = 0
          touchendX = 0
          touchstarttime = 0
          prevtouchendtime = 0
          turnsInCurrentSwipe = []
        },
        false
      )

      function handleGesture(isSingletouch = false): boolean {
        const MAX_TIME = isSingletouch
          ? MAX_TIME_SINGLETOUCH
          : MAX_TIME_TOUCHMOVE
        const MIN_DISTANCE = isSingletouch
          ? MIN_DISTANCE_SINGLETOUCH
          : MIN_DISTANCE_TOUCHMOVE
        const MIN_RATIO = isSingletouch
          ? MIN_RATIO_SINGLETOUCH
          : MIN_RATIO_TOUCHMOVE

        // max time
        if (
          prevtouchendtime &&
          performance.now() - prevtouchendtime > MAX_TIME
        ) {
          touchstartX = touchendX
          touchstartY = touchendY
          touchstarttime = performance.now()
        }
        if (performance.now() - touchstarttime > MAX_TIME) return false

        const vertChange = touchendY - touchstartY
        const horizChange = touchendX - touchstartX

        // min distance
        if (
          Math.abs(vertChange) < MIN_DISTANCE &&
          Math.abs(horizChange) < MIN_DISTANCE
        ) {
          return false
        }

        let isVertical: boolean
        if (MIN_RATIO < 1) {
          const isCurrentlyVertical =
            currentDirection.value === Direction.up ||
            currentDirection.value === Direction.down
          isVertical = !isCurrentlyVertical
        } else {
          isVertical = Math.abs(vertChange) >= Math.abs(horizChange)
        }

        // min ratio
        if (isVertical) {
          if (Math.abs(vertChange) / Math.abs(horizChange) < MIN_RATIO) {
            return false
          }
          doTurn(vertChange >= 0 ? Direction.down : Direction.up)
          return true
        } else {
          if (Math.abs(horizChange) / Math.abs(vertChange) < MIN_RATIO) {
            return false
          }
          doTurn(horizChange >= 0 ? Direction.right : Direction.left)
          return true
        }
      }

      let turnsInCurrentSwipe: { time: number; direction: Direction }[] = []
      function doTurn(direction: Direction) {
        if (direction !== currentDirection.value) {
          const time = performance.now()
          const oppositeDirTurn = turnsInCurrentSwipe.find(
            (turn) =>
              time - turn.time <= 800 &&
              turn.direction === oppositeDirection(direction)
          )
          if (oppositeDirTurn) {
            const prevDirTurn = turnsInCurrentSwipe.find(
              (turn) =>
                turn.time > oppositeDirTurn.time &&
                turn.direction !== direction &&
                turn.direction !== oppositeDirection(direction)
            )
            if (prevDirTurn) return
          }
          turnsInCurrentSwipe.push({ time, direction })
        }
        turn(direction)
      }
    }
  })
}
